import { useEffect, useState } from 'react'
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated'
import { Button, Dialog } from 'tamagui'
import { OverallRating } from './OverallRating'
import { UserSurveyResponseType } from '@api/mesa/graphql'
import { ThankYou } from './ThankYou'
import * as Linking from 'expo-linking'
import Close from '@/src/assets/icons/Close'
import { DetailedSurvey, DetailedSurveyResponse } from './DetailedSurvey'
import { SimpleUpDownRatingValue } from '../SimpleUpDownRating'
import { useSegmentDomain } from '@/src/services/segment/SegmentDomainProvider'
import { SurveysE2E } from '../../e2e'
import { AdditionalComments } from './AdditionalComments'

type SurveyResponseData = {
  overallRating?: number
  didOpenStore: boolean
  appExperience?: SimpleUpDownRatingValue
  cardValue?: SimpleUpDownRatingValue
  cardRedemptions?: SimpleUpDownRatingValue
  support?: SimpleUpDownRatingValue
  partnersAndBenefits?: SimpleUpDownRatingValue
  additionalComments?: string
}

const TAMAGUI_ANIMATION_DURATION = 300
const TAMAGUI_ANIMATION_DURATION_BUFFER = 100

export const SurveyResponseDataInitial = {
  didOpenStore: false,
} satisfies SurveyResponseData

export const Survey2025June = ({
  onComplete,
}: {
  onComplete: (responseType: UserSurveyResponseType, responseData: SurveyResponseData) => void
}) => {
  const [isOpen, setIsOpen] = useState(true)
  const [surveyScreen, setSurveyScreen] = useState<
    'overall-rating' | 'detailed-rating' | 'additional-comments' | 'thank-you'
  >('overall-rating')
  const [surveyResponseType, setSurveyResponseType] = useState<UserSurveyResponseType>(
    UserSurveyResponseType.DismissedSurvey,
  )
  const [surveyResponseData, setSurveyResponseData] =
    useState<SurveyResponseData>(SurveyResponseDataInitial)
  const { triggerAction } = useSegmentDomain()

  useEffect(() => {
    void triggerAction('SurveyForm', 'Opened')
  }, [triggerAction])

  useEffect(() => {
    void triggerAction('SurveyForm', 'ViewChanged', {
      surveyScreen,
    })
  }, [triggerAction, surveyScreen])

  const waitAndComplete = (
    responseType: UserSurveyResponseType,
    responseData: SurveyResponseData,
    callback?: () => void,
  ) => {
    // This is a workaround for the survey sheet background remaining visible
    // even after the component is unmounted if the unmount occurs before the
    // animation is complete.
    // Hopefully this won't be a problem for Tamagui in the future...
    setIsOpen(false)

    setTimeout(() => {
      onComplete(responseType, responseData)
      callback?.()
    }, TAMAGUI_ANIMATION_DURATION + TAMAGUI_ANIMATION_DURATION_BUFFER)
  }

  const handleSubmitOverallRating = (overallRating: number) => {
    setSurveyResponseType(UserSurveyResponseType.CompletedSurvey)
    setSurveyResponseData({
      ...surveyResponseData,
      overallRating,
    })

    if (overallRating === 5) {
      setSurveyScreen('thank-you')
    } else {
      setSurveyScreen('detailed-rating')
    }
  }

  const handleOpenAppStore = (storeUrl: string | undefined) => {
    waitAndComplete(
      surveyResponseType,
      {
        ...surveyResponseData,
        didOpenStore: true,
      },
      () => {
        if (storeUrl) {
          void Linking.openURL(storeUrl)
        }
      },
    )
  }

  const handleDismissThankYou = () => {
    waitAndComplete(surveyResponseType, {
      ...surveyResponseData,
      didOpenStore: false,
    })
  }

  const handleSubmitDetailedRating = (detailedSurveyResponse: DetailedSurveyResponse) => {
    // TODO: Fix the inputs on Android inside the sheet. I suspect a Tamagui issue.
    // The Submit button doesn't work either.
    waitAndComplete(surveyResponseType, {
      ...surveyResponseData,
      ...detailedSurveyResponse,
    })
  }

  const handleSubmitAdditionalComments = ({
    additionalComments,
  }: {
    additionalComments: string
  }) => {
    waitAndComplete(surveyResponseType, {
      ...surveyResponseData,
      additionalComments,
    })
  }

  const handleDialogOpenChange = (newOpenState: boolean) => {
    if (!newOpenState) {
      waitAndComplete(surveyResponseType, surveyResponseData)
    }
  }

  return (
    <Dialog modal defaultOpen open={isOpen} onOpenChange={handleDialogOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          key="overlay"
          animateOnly={['transform', 'opacity']}
          animation={[
            'quicker',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
          style={{ backgroundColor: 'black', opacity: 0.5 }}
          testID={SurveysE2E.Ids.View.DialogOverlay}
        />
        <Dialog.Content key="content" p="$4" gap="$4" pb="$8">
          {surveyScreen === 'overall-rating' && (
            <Animated.View
              entering={FadeIn}
              exiting={FadeOut}
              testID={SurveysE2E.Ids.View.OverallRating}
            >
              <OverallRating onSubmit={handleSubmitOverallRating} />
            </Animated.View>
          )}
          {surveyScreen === 'detailed-rating' && (
            <Animated.View
              entering={FadeIn}
              exiting={FadeOut}
              testID={SurveysE2E.Ids.View.DetailedRating}
            >
              <DetailedSurvey onSubmit={handleSubmitDetailedRating} />
            </Animated.View>
          )}
          {surveyScreen === 'additional-comments' && (
            <Animated.View
              entering={FadeIn}
              exiting={FadeOut}
              testID={SurveysE2E.Ids.View.AdditionalComments}
            >
              <AdditionalComments onSubmit={handleSubmitAdditionalComments} />
            </Animated.View>
          )}
          {surveyScreen === 'thank-you' && (
            <Animated.View
              entering={FadeIn}
              exiting={FadeOut}
              testID={SurveysE2E.Ids.View.ThankYou}
            >
              <ThankYou
                onSubmit={(storeUrl) => handleOpenAppStore(storeUrl)}
                onDismiss={handleDismissThankYou}
              />
            </Animated.View>
          )}
          <Dialog.Close asChild>
            <Button
              r="$2"
              t="$2"
              position="absolute"
              size="$2"
              circular
              icon={<Close width={16} height={16} />}
              testID={SurveysE2E.Ids.Press.DialogClose}
            />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>

      <Dialog.Adapt when="maxMd" platform="touch">
        <Dialog.Sheet
          animation="medium"
          zIndex={200000}
          dismissOnSnapToBottom
          dismissOnOverlayPress
          modal
          snapPointsMode="fit"
          moveOnKeyboardChange
        >
          <Dialog.Sheet.Handle />
          <Dialog.Sheet.Frame p="$4" gap="$4" pb="$8">
            <Dialog.Adapt.Contents />
          </Dialog.Sheet.Frame>
          <Dialog.Sheet.Overlay
            style={{ backgroundColor: 'black', opacity: 0.5 }}
            animation="lazy"
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
            testID={SurveysE2E.Ids.View.DialogOverlay}
          />
        </Dialog.Sheet>
      </Dialog.Adapt>
    </Dialog>
  )
}
